import { useEffect } from "react";

import { SignedOutCard } from "@don/design-system";

import { WORDMARK } from "../shell/ConsoleShell.js";
import { ADMIN_PAGE_COPY, COPY } from "./copy.js";

/**
 * Where Microsoft sends the IT admin after the admin consent link (issue 466, Outlook design-spec
 * §2.2, §5.2): the api's `/api/connect/microsoft/admin-callback` reads the token and 303s here
 * with `?r=approved|failed|unknown-org` and, for unknown-org, the refused domain in `d`, never the
 * token. Mounted by `main.tsx` outside the owner shell: no owner read, no `/connect/me`, no
 * Employee session (the admin is not a Don user), and the reporter stays silent (`reportableAt`).
 */
export type AdminResult = { readonly kind: "approved" | "failed" } | { readonly kind: "unknown-org"; readonly domain: string };

/**
 * The result a 303's query names (`connect.test.ts`). Anything else, and unknown-org without a
 * plain DNS name in `d`, is `failed`, so the page never shows text it was handed that is not a
 * domain.
 */
export function adminResultOf(search: string): AdminResult {
  const query = new URLSearchParams(search);
  switch (query.get("r")) {
    case "approved":
      return { kind: "approved" };
    case "unknown-org": {
      const domain = (query.get("d") ?? "").toLowerCase();
      return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? { kind: "unknown-org", domain } : { kind: "failed" };
    }
    default:
      return { kind: "failed" };
  }
}

/** The card for `result` (§4.2, §6.2): the wordmark, the title, one body line; no controls. */
export function AdminCallbackScreen({ result }: { result: AdminResult }) {
  const [title, body] =
    result.kind === "unknown-org"
      ? [ADMIN_PAGE_COPY.unknownTitle(result.domain), ADMIN_PAGE_COPY.unknownBody]
      : result.kind === "approved"
        ? [ADMIN_PAGE_COPY.approvedTitle, ADMIN_PAGE_COPY.approvedBody]
        : [ADMIN_PAGE_COPY.failedTitle, ADMIN_PAGE_COPY.failedBody];
  return (
    <SignedOutCard wordmark={WORDMARK} title={title} hook="data-admin-callback" cardHook={`data-admin-result-${result.kind}`}>
      <p
        style={{
          margin: 0, marginTop: -12, maxWidth: "34ch", color: "var(--ink-2)",
          fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
        }}
      >
        {body}
      </p>
    </SignedOutCard>
  );
}

/** The page: reads the URL once, titles the tab "Don" (§2.2). */
export function AdminCallbackApp({ search }: { search: string }) {
  useEffect(() => {
    document.title = COPY.adminTabTitle;
  }, []);
  return <AdminCallbackScreen result={adminResultOf(search)} />;
}
